/**
 * X-Request-Id 生成 —— 前端请求与服务端日志对账的标识（P1-logging-observability：request-id 贯穿）。
 *
 * Why 不各处直接调 `crypto.randomUUID()`：它要求**安全上下文**（HTTPS / localhost）且需较新浏览器
 * （Chrome 92+ / Safari 15.4+）。不满足时该属性为 undefined，直接调用抛 TypeError——且抛在
 * 请求**发出之前**，整个操作表现为通用「操作失败」，与网络故障、密码错误无从区分，排障方向被带偏。
 *
 * request id 只服务于排障，绝不该决定请求能否发出：故按 randomUUID → getRandomValues → 时间戳+随机数
 * 逐级退化。退化值不保证全局唯一，但足以在日志里定位单次请求。
 */
export function newRequestId(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    if (typeof crypto.getRandomValues === 'function') {
      return formatAsUuidV4(crypto.getRandomValues(new Uint8Array(16)));
    }
  }
  // 连 crypto 都没有：Math.random 填充——仅用于日志关联，不承担任何安全语义
  const bytes = new Uint8Array(16);
  for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return formatAsUuidV4(bytes);
}

/**
 * 按 UUID v4 形状（含连字符 + version/variant 位）格式化 16 字节。
 *
 * Why 必须是合法 UUID 形状：服务端 request-id 中间件按正则校验，形状不符会**静默替换**成
 * 服务端自生成的 id（`packages/server/src/middleware/request-id.ts`，因 `admin_audit_logs.request_id`
 * 是 PG uuid 列）。裸 hex 串虽然唯一，却会在入口被丢弃 —— 本模块承诺的「客户端↔服务端日志关联」
 * 恰恰在最需要它的降级路径上失效。
 */
function formatAsUuidV4(bytes: Uint8Array): string {
  const b = Array.from(bytes);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40; // version 4
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80; // variant 10xx
  const hex = b.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
